import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Archive, Loader2, CheckCircle2, XCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import StorageMeter, { mb } from "@/components/StorageMeter";
import { Button } from "@/components/ui/button";
import { API, sel } from "@/lib/purchases";
import { useAuth } from "@/context/AuthContext";

const E = `${API}/purchases/export`;
const years = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2, y - 3, y - 4]; };

function Job({ j }) {
  const pct = j.total ? Math.round((j.done / j.total) * 100) : 0;
  const dl = async () => {
    try {
      const { data } = await axios.get(`${E}/${j.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(data); const a = Object.assign(document.createElement("a"), { href: u, download: `HupLeyHuat-records-${j.year}.zip` }); a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Download failed — the export may have expired; build it again"); }
  };
  return (
    <div className="rounded-xl border border-slate-800 bg-[#151D2E] p-4 space-y-2" data-testid={`export-job-${j.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-bold">
          {j.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : j.status === "failed" ? <XCircle className="w-4 h-4 text-rose-400" /> : <Loader2 className="w-4 h-4 text-amber-300 animate-spin" />}
          FY{j.year}{j.include_claims ? " + insurance claims" : ""}
        </div>
        <span className="text-xs text-slate-500 font-mono-num" data-testid="export-status">{j.status === "done" ? `${j.bills} bills · ${j.claims || 0} claims · ${mb(j.size)}` : j.status === "failed" ? "Failed" : `${j.done}/${j.total} files`}</span>
      </div>
      {j.status !== "done" && j.status !== "failed" && <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${Math.max(3, pct)}%` }} /></div>}
      {j.status === "failed" && <p className="text-xs text-rose-300">{j.error}</p>}
      {j.status === "done" && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">{j.missing ? <span className="text-amber-300">{j.missing} file(s) could not be fetched — listed in README.txt</span> : "All receipt files included · bills workbook inside"}</p>
          <Button onClick={dl} data-testid="export-download" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-9"><Download className="w-4 h-4 mr-1.5" /> Download ZIP</Button>
        </div>
      )}
    </div>
  );
}

export default function ExportPage() {
  const { isOwner } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [claims, setClaims] = useState(true);
  const [jobs, setJobs] = useState([]);
  const timer = useRef();
  const load = () => axios.get(E).then(({ data }) => setJobs(data)).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(timer.current); }, []);
  useEffect(() => {
    clearInterval(timer.current);
    if (jobs.some((j) => j.status === "queued" || j.status === "running")) timer.current = setInterval(load, 1500);
    return () => clearInterval(timer.current);
  }, [jobs]);
  const start = async () => {
    try { await axios.post(E, { year: Number(year), include_claims: claims }); toast.success(`Building FY${year} export…`); load(); }
    catch (e) { toast.error("Could not start export", { description: e?.response?.data?.detail }); }
  };
  const busy = jobs.some((j) => j.status === "queued" || j.status === "running");
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-24 md:pb-8">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="export-page">
        <div><p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold">Bills & Purchases · Records</p><h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mt-1">Yearly Export</h1><p className="text-slate-400 mt-1 text-sm">One ZIP per financial year: an Excel workbook of every bill and payment, plus every original receipt photo/PDF named by date, supplier and invoice number. Keep it offline — IRAS requires records for 5 years.</p></div>
        <StorageMeter />
        {isOwner ? (
          <section className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent p-5 space-y-4">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1.5"><label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Financial year</label><select value={year} onChange={(e) => setYear(e.target.value)} data-testid="export-year" className={sel}>{years().map((y) => <option key={y} value={y}>{y}</option>)}</select></div>
              <Button onClick={start} disabled={busy} data-testid="export-start" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-10 px-6"><Archive className="w-4 h-4 mr-2" /> {busy ? "Building…" : "Build export"}</Button>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" checked={claims} onChange={(e) => setClaims(e.target.checked)} data-testid="export-include-claims" className="accent-emerald-400 w-4 h-4" /> Include insurance-claim documents (by accident date)</label>
          </section>
        ) : <p className="text-sm text-slate-500 rounded-xl border border-slate-800 p-4">Only the owner can build record exports.</p>}
        <section className="space-y-2">
          <h2 className="text-base md:text-lg font-bold">Exports built this session</h2>
          {jobs.length === 0 ? <p className="text-sm text-slate-500" data-testid="export-empty">None yet. Exports are kept in memory until the server restarts — download as soon as it's ready.</p> : jobs.map((j) => <Job key={j.id} j={j} />)}
        </section>
      </main>
    </div>
  );
}
